(() => {
    const tagName = 'er-site-header';
    if (customElements.get(tagName)) return;

    class ERSiteHeader extends HTMLElement {
      connectedCallback() {
        if (this.events || this.initFrame != null) return;
        this.initFrame = requestAnimationFrame(() => {
          this.initFrame = null;
          if (this.isConnected) this.initialize();
        });
      }

      initialize() {
        if (this.events) return;
        this.events = new AbortController();
        const { signal } = this.events;
        this.drawer = this.querySelector('[data-menu-drawer]');
        this.openButton = this.querySelector('[data-menu-open]');
        this.closeButton = this.querySelector('[data-menu-close]');
        this.overlay = this.querySelector('[data-menu-overlay]');
        this.lastFocused = null;
        this.scrollFrame = null;
        this.focusFrame = null;
        this.lastScrollState = null;
        this.desktopQuery = window.matchMedia('(min-width: 1200px)');
        this.desktopSources = new WeakMap();
        this.stickyShell = this.closest('.shopify-section');
        this.stickyShell?.classList.add('header-glass-shell');
        if (this.dataset.sticky === 'true') {
          this.stickyShell?.classList.add('header-sticky-shell');
        }

        this.openButton?.addEventListener('click', () => {
          if (this.classList.contains('is-menu-open')) this.closeMenu();
          else this.openMenu();
        }, { signal });
        this.closeButton?.addEventListener('click', () => this.closeMenu(), { signal });
        this.overlay?.addEventListener('click', () => this.closeMenu(), { signal });
        this.querySelectorAll('[data-mobile-toggle]').forEach((button) => {
          button.addEventListener('click', () => this.toggleMobileItem(button), { signal });
        });
        this.querySelectorAll('[data-desktop-toggle]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggleDesktopItem(button);
          }, { signal });
        });
        this.querySelectorAll('[data-desktop-item]').forEach((item) => {
          item.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse' || !this.desktopQuery.matches) return;
            this.closeDesktopMenus(item);
            if (!item.classList.contains('is-open')) this.setDesktopItem(item, true, 'hover');
          }, { signal });
          item.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse' && this.desktopSources.get(item) === 'hover' && !item.contains(document.activeElement)) {
              this.setDesktopItem(item, false);
            }
          }, { signal });
          item.addEventListener('focusout', (event) => {
            if (!item.contains(event.relatedTarget)) this.setDesktopItem(item, false);
          }, { signal });
        });
        this.querySelectorAll('.site-header__drawer a').forEach((link) => {
          link.addEventListener('click', () => this.closeMenu(false), { signal });
        });
        document.addEventListener('click', (event) => this.handleDocumentClick(event), { signal });
        document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
        window.addEventListener('resize', () => this.handleResize(), { signal, passive: true });
        window.addEventListener('scroll', () => this.handleScroll(), { signal, passive: true });
        this.updateScrollState();
        this.openCurrentMobileBranch();
        this.closeDesktopMenus();
        this.drawer?.setAttribute('inert', '');
        this.classList.add('is-initialized');
      }

      disconnectedCallback() {
        if (this.initFrame != null) cancelAnimationFrame(this.initFrame);
        this.initFrame = null;
        this.events?.abort();
        this.events = null;
        if (this.scrollFrame != null) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
        this.closeMenu(false);
        this.closeDesktopMenus();
        this.classList.remove('is-initialized');
        this.stickyShell?.classList.remove('header-sticky-shell', 'header-glass-shell');
      }

      openMenu() {
        if (!this.drawer || !this.openButton || this.desktopQuery.matches || this.classList.contains('is-menu-open')) return;
        this.closeDesktopMenus();
        this.lastFocused = document.activeElement;
        this.drawer.removeAttribute('inert');
        this.classList.add('is-menu-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.openButton.setAttribute('aria-expanded', 'true');
        this.openButton.setAttribute('aria-label', 'Close menu');
        document.body?.classList.add('header-menu-open');
        this.focusFrame = requestAnimationFrame(() => {
          this.focusFrame = requestAnimationFrame(() => {
            this.focusFrame = null;
            if (this.isConnected && this.classList.contains('is-menu-open')) this.closeButton?.focus({ preventScroll: true });
          });
        });
      }

      closeMenu(restoreFocus = true) {
        if (this.focusFrame != null) cancelAnimationFrame(this.focusFrame);
        this.focusFrame = null;
        if (!this.drawer || !this.openButton) return;
        const wasOpen = this.classList.contains('is-menu-open');
        if (wasOpen && restoreFocus && this.lastFocused instanceof HTMLElement && this.lastFocused.isConnected) {
          this.lastFocused.focus();
        } else if (this.drawer.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        this.classList.remove('is-menu-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.setAttribute('inert', '');
        this.openButton.setAttribute('aria-expanded', 'false');
        this.openButton.setAttribute('aria-label', 'Open menu');
        document.body?.classList.toggle('header-menu-open', Boolean(document.querySelector('er-site-header.is-menu-open')));
        this.lastFocused = null;
      }

      toggleMobileItem(button) {
        const item = button.closest('[data-mobile-item]');
        if (!item) return;

        this.setMobileItem(item, !item.classList.contains('is-open'));
      }

      setMobileItem(item, isOpen) {
        const button = item.querySelector('[data-mobile-toggle]');
        const panel = item.querySelector('.site-header__mobile-submenu');
        if (!button || !panel) return;
        if (!isOpen && panel.contains(document.activeElement)) button.focus();
        item.classList.toggle('is-open', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
        panel.setAttribute('aria-hidden', String(!isOpen));
        panel.toggleAttribute('inert', !isOpen);
      }

      openCurrentMobileBranch() {
        this.querySelectorAll('[data-mobile-item]').forEach((item) => {
          this.setMobileItem(item, item.classList.contains('is-active'));
        });
      }

      toggleDesktopItem(button) {
        const item = button.closest('[data-desktop-item]');
        if (!item || !this.desktopQuery.matches) return;

        const willOpen = !item.classList.contains('is-open') || this.desktopSources.get(item) === 'hover';
        this.closeDesktopMenus(item);
        this.setDesktopItem(item, willOpen);
      }

      setDesktopItem(item, isOpen, source = 'toggle') {
        const button = item.querySelector('[data-desktop-toggle]');
        const panel = item.querySelector('[data-desktop-panel]');
        if (!button || !panel) return;
        if (isOpen) this.desktopSources.set(item, source);
        else this.desktopSources?.delete(item);
        item.classList.toggle('is-open', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
        panel.setAttribute('aria-hidden', String(!isOpen));
        panel.toggleAttribute('inert', !isOpen);
      }

      closeDesktopMenus(except = null) {
        this.querySelectorAll('[data-desktop-item]').forEach((item) => {
          if (item === except) return;
          this.setDesktopItem(item, false);
        });
      }

      handleDocumentClick(event) {
        const item = event.target instanceof Element ? event.target.closest('[data-desktop-item]') : null;
        this.closeDesktopMenus(item && this.contains(item) ? item : null);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          if (this.classList.contains('is-menu-open')) {
            event.preventDefault();
            this.closeMenu();
            return;
          }
          const item = document.activeElement?.closest('[data-desktop-item].is-open');
          if (item && this.contains(item)) {
            event.preventDefault();
            item.querySelector('[data-desktop-toggle]')?.focus();
          }
          this.closeDesktopMenus();
        }

        if (event.key === 'Tab' && this.classList.contains('is-menu-open')) {
          this.trapFocus(event);
        }
      }

      trapFocus(event) {
        if (!this.drawer) return;
        const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => !element.closest('[inert]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');

        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (!this.drawer.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleResize() {
        if (this.desktopQuery.matches && this.classList.contains('is-menu-open')) {
          this.closeMenu(false);
        }
        if (!this.desktopQuery.matches) this.closeDesktopMenus();
        this.handleScroll();
      }

      updateScrollState() {
        const isScrolled = window.scrollY > 10;
        if (isScrolled !== this.lastScrollState) {
          this.lastScrollState = isScrolled;
          this.classList.toggle('is-scrolled', isScrolled);
        }
      }

      handleScroll() {
        if (this.scrollFrame !== null) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (this.isConnected) this.updateScrollState();
        });
      }
    }

    customElements.define(tagName, ERSiteHeader);
  })();
